import { FC, PropsWithChildren, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, createContext, useContext } from 'react';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button } from 'react-daisyui';

type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  closeable?: boolean;
};

const ModalContext = createContext<string | null>(null);

const Modal: FC<PropsWithChildren<PropsWithClassName<ModalProps>>> = ({
  onClose,
  isOpen,
  children,
  showCloseButton = false,
  className,
  closeable = true,
}) => {
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const id = useId();

  const handleClose = useCallback(() => {
    if (modalRef.current) {
      modalRef.current.close();
    }
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  useLayoutEffect(() => {
    if (!modalRef.current) return;

    if (isOpen) {
      modalRef.current.showModal();
    } else {
      modalRef.current.close();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleCloseFn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!closeable) {
          e.preventDefault();
          return;
        }
        handleClose();
      }
    };

    document.addEventListener('keydown', handleCloseFn, false);

    return () => {
      document.removeEventListener('keydown', handleCloseFn, false);
    };
  }, [handleClose, closeable]);

  const modalNavId = `modal-nav-${id}`;

  return (
    <ModalContext.Provider value={modalNavId}>
      <dialog ref={modalRef} id={id} className="modal">
        <div className={cn('modal-box relative pb-0 bg-base-100', className)}>
          {showCloseButton && (
            <div className="absolute right-4 top-4">
              <Button onClick={handleClose} color="ghost" shape="circle" size="xs">
                <XMarkIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
          <div className="pb-1">{children}</div>
          <div
            className="flex gap-4 pt-4 pb-6 justify-between flex-wrap sticky bottom-0 bg-base-100"
            id={modalNavId}
          ></div>
        </div>

        <div className="modal-backdrop">
          <button onClick={closeable ? handleClose : undefined}>close</button>
        </div>
      </dialog>
    </ModalContext.Provider>
  );
};

const ModalNav: FC<PropsWithChildren> = ({ children }) => {
  const [watch, setWatch] = useState(false);
  const modalNavId = useContext(ModalContext);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  if (!modalNavId) {
    console.error('ModalNav must be used within a Modal');
    return null;
  }

  return (
    <Portal watch={watch} elementId={modalNavId}>
      {children}
    </Portal>
  );
};

export default {
  Wrapper: Modal,
  Nav: ModalNav,
};
